import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import routes from "../../../../Routes/routes";
import {errorMsg} from "../../../../Apis/functions";
import {CreateZoneAccess, GetZoneAccessById, UpdateZoneAccess} from "../../../../reduxToolkit/Guards/GuardsApi";
import {GUARDS_TABS, ZONE_ACCESS_DATA, ZONE_ACCESS_PERMISSION, ZONE_ACCESS_STEPS} from "../Model/Model";

/**
 * Joins the assigned items with the ones picked to add, without repeating any.
 *
 * @param {Array} assigned Items already assigned.
 * @param {Array} selected Items picked to add.
 * @param {string} keyId Key that identifies an item.
 */
const mergeItems = (assigned, selected, keyId) => [
    ...assigned,
    ...selected.filter((item) => !assigned.some((e) => String(e[keyId]) === String(item[keyId]))),
];

/**
 * Creates a zone access in three steps, its zones, its guards and a summary, or updates it with
 * the zones and the guards as tabs. On the update, the zones and the guards already assigned are
 * kept apart from the ones picked to add, and both are saved together.
 */
const ZoneAccessFormApp = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {id} = useParams();
    const isUpdate = !!id;
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const [activeStep, setActiveStep] = useState(ZONE_ACCESS_STEPS.ZONES);
    const [name, setName] = useState('');
    // Selected in each step; they live here because they are validated, saved and shown in the summary
    const [selectedZones, setSelectedZones] = useState([]);
    const [selectedGuards, setSelectedGuards] = useState([]);
    // Assigned to the zone access being updated
    const [assignedZones, setAssignedZones] = useState([]);
    const [assignedGuards, setAssignedGuards] = useState([]);
    // The steps wait for the zone access being updated, so they mount with its assigned items and do
    // not read their available ones again when those arrive
    const [loaded, setLoaded] = useState(!isUpdate);
    // Name as saved, sent when assigned items are removed so an unsaved edit of the name is not saved
    const [savedName, setSavedName] = useState('');
    const [emptyFields, setEmptyFields] = useState(false);

    const canSave = DEV || permission?.includes(isUpdate ? ZONE_ACCESS_PERMISSION.UPDATE : ZONE_ACCESS_PERMISSION.CREATE);
    const canRead = DEV || permission?.includes(ZONE_ACCESS_PERMISSION.READ);
    // Without the update permission, a zone access is only read
    const disabled = isUpdate && !canSave;

    useEffect(() => {
        if (!canSave && !(isUpdate && canRead)) navigate(routes.forbidden);
    }, [canSave, canRead]);

    useEffect(() => {
        if (isUpdate) {
            dispatch(GetZoneAccessById(id)).then(({payload: {data: {data, success}}}) => {
                if (success) {
                    setName(data?.name || '');
                    setSavedName(data?.name || '');
                    setAssignedZones(data?.zones || []);
                    setAssignedGuards(data?.guards || []);
                }
                setLoaded(true);
            });
        }
    }, [id]);

    const allZones = mergeItems(assignedZones, selectedZones, 'id');
    const allGuards = mergeItems(assignedGuards, selectedGuards, 'userId');

    // The guards are optional, only the name and the zones are required
    const isZonesValid = name.trim() !== '' && allZones.length > 0;

    /**
     * Moves to the next step once the current one is valid.
     */
    const handleNext = () => {
        const valid = activeStep === ZONE_ACCESS_STEPS.ZONES ? isZonesValid : true;
        setEmptyFields(!valid);
        if (valid) setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => setActiveStep((prev) => prev - 1);

    /**
     * Clears the name and the selected zones of the zones step.
     */
    const handleClearFields = () => {
        setName('');
        setSelectedZones([]);
        setEmptyFields(false);
    };

    const goBackToList = () => navigate(routes.guards.list, {state: {currentTab: GUARDS_TABS.ZONE_ACCESS.INDEX}});

    const handleSave = () => {
        if (!isZonesValid) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }
        const body = ZONE_ACCESS_DATA.set(name, allZones, allGuards);
        const request = isUpdate ? UpdateZoneAccess({id, body}) : CreateZoneAccess(body);

        dispatch(request).then(({payload: {data: {success, code}}}) => {
            if (success) {
                toast.success(t(isUpdate ? 'has_been_updated_successfully' : 'has_been_created_successfully'));
                if (!isUpdate) {
                    goBackToList();
                    return;
                }
                // An update keeps the user on the form, with the picked items now assigned
                setSavedName(body.name);
                setAssignedZones(allZones);
                setAssignedGuards(allGuards);
                setSelectedZones([]);
                setSelectedGuards([]);
                setEmptyFields(false);
            } else errorMsg(code);
        });
    };

    /**
     * Removes assigned zones or guards right away, saving the zone access with the ones that remain.
     * The items picked to add are not saved. The service requires at least one zone, the guards are
     * optional.
     *
     * @param {Array} zones Zones that remain assigned.
     * @param {Array} guards Guards that remain assigned.
     * @param {Function} onDone Called once the items are removed.
     */
    const saveRemainingAssigned = (zones, guards, onDone) => {
        if (zones.length === 0) {
            toast.warn(t('zone_access_must_keep_at_least_one_zone'));
            return;
        }
        const body = ZONE_ACCESS_DATA.set(savedName, zones, guards);

        dispatch(UpdateZoneAccess({id, body})).then(({payload: {data: {success, code}}}) => {
            if (success) {
                toast.success(t('has_been_deleted_successfully'));
                setAssignedZones(zones);
                setAssignedGuards(guards);
                onDone();
            } else errorMsg(code);
        });
    };

    const removeAssignedZones = (zoneIds, onDone) =>
        saveRemainingAssigned(assignedZones.filter((zone) => !zoneIds.includes(zone.id)), assignedGuards, onDone);

    const removeAssignedGuards = (userIds, onDone) =>
        saveRemainingAssigned(assignedZones, assignedGuards.filter((guard) => !userIds.includes(guard.userId)), onDone);

    return {
        t,
        id,
        isUpdate,
        canSave,
        disabled,
        loaded,
        activeStep,
        setActiveStep,
        name,
        setName,
        selectedZones,
        setSelectedZones,
        selectedGuards,
        setSelectedGuards,
        assignedZones,
        assignedGuards,
        removeAssignedZones,
        removeAssignedGuards,
        emptyFields,
        handleNext,
        handleBack,
        handleClearFields,
        handleSave,
        goBackToList,
    };
};

export default ZoneAccessFormApp;
